/** Physician handwritten signature for reports — black ink on transparent PNG. */
export const PHYSICIAN_SIGNATURE_SOURCE_PATH = '/signatures/dr-signature-ar.png';
export const PHYSICIAN_SIGNATURE_PATH = '/signatures/dr-signature-ar-print.png';
export const PHYSICIAN_SIGNATURE_WIDTH_MM = 50;
export const PHYSICIAN_SIGNATURE_HEIGHT_MM = 33;

const LUM_INK_THRESHOLD = 0.5;
const INK_RGB = [0, 0, 0];

let _signatureDataUrlPromise = null;

const pixelLuminance = (data, i) =>
    0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

/**
 * Ensure every visible pixel is solid black with full alpha (no grey wash-out).
 * Idempotent for already-processed print assets.
 */
const binarizeSignatureForPrint = (dataUrl) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(dataUrl);
                return;
            }
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const { data } = imageData;

            for (let i = 0; i < data.length; i += 4) {
                const lum = pixelLuminance(data, i);
                if (lum >= LUM_INK_THRESHOLD) {
                    data[i] = INK_RGB[0];
                    data[i + 1] = INK_RGB[1];
                    data[i + 2] = INK_RGB[2];
                    data[i + 3] = 255;
                } else {
                    data[i + 3] = 0;
                }
            }

            ctx.putImageData(imageData, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => reject(new Error('Failed to load signature image'));
        img.src = dataUrl;
    });

const resolvePhysicianSignaturePath = () => {
    const override =
        typeof globalThis !== 'undefined' && globalThis.__PORTAL_PDF_SIGNATURE_PATH__
            ? globalThis.__PORTAL_PDF_SIGNATURE_PATH__
            : null;
    return override || PHYSICIAN_SIGNATURE_PATH;
};

export const getPhysicianSignatureDataUrl = async () => {
    if (!_signatureDataUrlPromise) {
        _signatureDataUrlPromise = (async () => {
            const signaturePath = resolvePhysicianSignaturePath();
            const res = await fetch(signaturePath);
            if (!res.ok) throw new Error(`Signature asset not found: ${signaturePath}`);
            const blob = await res.blob();
            const reader = new FileReader();
            const rawDataUrl = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            return binarizeSignatureForPrint(rawDataUrl);
        })().catch((err) => {
            console.error('Physician signature load failed:', err);
            _signatureDataUrlPromise = null;
            return null;
        });
    }
    return _signatureDataUrlPromise;
};

/**
 * Draw signature image above footer chrome. Returns Y after the block.
 * @param {import('jspdf').jsPDF} doc
 */
export const addPhysicianSignatureToPdf = async (doc, pageWidth, startY, options = {}) => {
    const {
        isArabic = false,
        align = 'right',
        marginRight = null,
        marginLeft = null,
        widthMm = PHYSICIAN_SIGNATURE_WIDTH_MM,
        heightMm = PHYSICIAN_SIGNATURE_HEIGHT_MM,
        maxY = null,
    } = options;

    const dataUrl = await getPhysicianSignatureDataUrl();
    if (!dataUrl) return startY;

    const y = startY + 2;
    if (maxY != null && y + heightMm > maxY) {
        return startY;
    }

    const rightMargin = marginRight ?? 30;
    const leftMargin = marginLeft ?? 10;
    const x =
        align === 'left'
            ? leftMargin
            : align === 'center'
              ? (pageWidth - widthMm) / 2
              : pageWidth - rightMargin - widthMm;

    try {
        doc.addImage(dataUrl, 'PNG', x, y, widthMm, heightMm, undefined, 'FAST');
    } catch (e) {
        console.error('addImage signature error', e);
        return startY;
    }

    doc.setFontSize(7);
    doc.setTextColor(80);
    const caption = isArabic ? 'د. محمود سامي أبوزيد' : 'Dr. Mahmoud Sami Abouzeid';
    doc.text(caption, x + widthMm / 2, y + heightMm + 3.5, { align: 'center' });
    doc.setTextColor(0);

    return y + heightMm + 6;
};
